/**
 * ProfilePage - Artist/Listener Profile Page
 * Shows profile info, tracks, stats, followers, and top artists
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Music, Users, Play, Heart, Settings, Edit2, Share2,
  MapPin, Calendar, ExternalLink, ChevronRight, Award,
  Flame, ThumbsDown, Guitar, FileText, Plus, MoreHorizontal,
  UserPlus, UserMinus, Check, Video, Image, MessageCircle, Camera, Loader2, Wallet
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { StoryGlowRing, StoryViewer, StoryCreator } from '../components/Stories';
import MediaUpload, { MediaGallery } from '../components/MediaUpload';
import { UserClipsGallery } from '../components/Clips';
import { useFloatingChat } from '../components/FloatingChatProvider';
import TipModal from '../components/TipModal';
import NotificationBell from '../components/NotificationBell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stats Card
const StatCard = ({ icon: Icon, value, label, color }) => (
  <div className="text-center">
    <div className={`w-11 h-11 mx-auto rounded-2xl flex items-center justify-center mb-2 shadow-lg ${color}`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <p className="text-2xl font-black text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>{value}</p>
    <p className="text-[#71717A] text-xs">{label}</p>
  </div>
);

// Track Row Component
const TrackRow = ({ track, rank, onPlay }) => (
  <motion.div 
    whileHover={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
    className="flex items-center gap-3 p-3 rounded-xl cursor-pointer"
    onClick={() => onPlay && onPlay(track)}
  >
    {rank && <span className="text-[#71717A] text-sm font-medium w-6">{rank}</span>}
    <div className="w-12 h-12 rounded-lg bg-white/10 overflow-hidden flex-shrink-0">
      {track.artwork ? (
        <img src={track.artwork} alt={track.track_title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Music className="w-5 h-5 text-[#71717A]" />
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium truncate">{track.track_title}</p>
      <p className="text-[#71717A] text-sm">{track.genre || 'Hip-Hop'}</p>
    </div>
    <div className="flex items-center gap-3 text-[#71717A] text-sm">
      <span className="flex items-center gap-1">
        <Play className="w-3.5 h-3.5" />
        {track.play_count?.toLocaleString() || 0}
      </span>
      <span className="flex items-center gap-1 text-green-400">
        <Flame className="w-3.5 h-3.5" />
        {track.vote_counts?.issa_hit || 0}
      </span>
    </div>
  </motion.div>
);

// Top Artist Card
const TopArtistCard = ({ artist, rank }) => (
  <div className="flex items-center gap-3 p-2">
    <span className="text-[#FF007F] font-bold w-5">{rank}</span>
    <div className="w-10 h-10 rounded-full bg-white/10 overflow-hidden">
      {artist.profile_picture ? (
        <img src={artist.profile_picture} alt={artist.stage_name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FF007F] to-[#00F0FF]">
          <span className="text-white text-sm font-bold">{artist.stage_name?.[0]}</span>
        </div>
      )}
    </div>
    <span className="text-white text-sm font-medium truncate">{artist.stage_name}</span>
  </div>
);

// Main Profile Page
const ProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, patchUser } = useAuth();
  const { openChat } = useFloatingChat();
  
  const [profile, setProfile] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [topArtists, setTopArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [friendStatus, setFriendStatus] = useState('none');
  const [friendReqId, setFriendReqId] = useState(null);
  const [friendLoading, setFriendLoading] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const [showStoryViewer, setShowStoryViewer] = useState(null);
  const [showStoryCreator, setShowStoryCreator] = useState(false);
  const [roleUpdating, setRoleUpdating] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editName, setEditName] = useState('');
  const [editBio, setEditBio] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const openEditProfile = () => {
    setEditName(profile?.stage_name || profile?.name || '');
    setEditBio(profile?.bio || '');
    setShowEditProfile(true);
  };

  const saveProfileEdits = async () => {
    const name = editName.trim();
    if (!name) { toast.error('Please enter a name'); return; }
    setSavingProfile(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.put(
        `${API_URL}/api/artists/profile/update`,
        { stage_name: name, bio: editBio },
        { headers }
      );
      setProfile((prev) => ({ ...prev, stage_name: name, bio: editBio }));
      if (patchUser) patchUser({ name });
      setShowEditProfile(false);
      toast.success('Profile updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const isOwner = !!(user?.isSuperAdmin || user?.role === 'super_admin');

  const changeRole = async (newRole) => {
    if (!profile || newRole === (profile.role || 'listener')) return;
    setRoleUpdating(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      if (isOwnProfile) {
        await axios.put(`${API_URL}/api/artists/profile/role`, { role: newRole }, { headers });
        if (patchUser) patchUser({ role: newRole === 'artist' ? 'artist' : 'user' });
      } else {
        await axios.put(`${API_URL}/api/admin/users/${profile.user_id}/role`, { role: newRole }, { headers });
      }
      setProfile((prev) => ({ ...prev, role: newRole }));
      toast.success(`Account set to ${newRole}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not change account type');
    } finally {
      setRoleUpdating(false);
    }
  };
  const [uploadingImage, setUploadingImage] = useState(null); // 'profile' | 'cover' | null
  const [mediaRefreshKey, setMediaRefreshKey] = useState(0);
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [stats, setStats] = useState({
    followers: 0,
    following: 0,
    totalStreams: 0,
    trackCount: 0
  });

  // Determine target user ID
  const targetUserId = userId || user?.id;

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!targetUserId && !isAuthenticated) {
        navigate('/auth');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        // Reset secondary state so a previously-viewed profile's tracks/stats
        // never bleed into the new one while background fetches are in flight.
        setTracks([]);
        setTopArtists([]);
        setStats({ followers: 0, following: 0, totalStreams: 0, trackCount: 0 });
        setIsFollowing(false);
        setFriendStatus('none');
        setFriendReqId(null);
        
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Fetch artist profile
        let profileData = null;
        
        if (!userId && isAuthenticated) {
          // Fetching own profile
          const res = await axios.get(`${API_URL}/api/artists/profile/me`, { headers });
          if (res.data?.profile) {
            profileData = res.data.profile;
            setIsOwnProfile(true);
          }
        } else if (userId) {
          // Fetching another user's profile
          const res = await axios.get(`${API_URL}/api/artists/profile/${userId}`, { headers });
          if (res.data?.profile) {
            profileData = res.data.profile;
            setIsOwnProfile(user?.id === userId);
          }
        }

        // Fallback: user has no artist profile (e.g. a listener). Build a
        // minimal profile from the basic user record so the page + message
        // button still render.
        if (!profileData) {
          const basicId = userId || user?.id;
          if (basicId) {
            try {
              const uRes = await axios.get(`${API_URL}/api/users/${basicId}`, { headers });
              const u = uRes.data;
              if (u) {
                profileData = {
                  user_id: u.id,
                  name: u.name,
                  username: u.username,
                  stage_name: u.name || u.username || 'Listener',
                  profile_picture: u.profilePhoto || u.googleProfilePicture,
                  cover_photo: null,
                  bio: u.bio || '',
                  location: [u.city, u.state].filter(Boolean).join(', ') || null,
                  role: u.role || 'listener',
                  is_artist: false,
                  genres: []
                };
                setIsOwnProfile(user?.id === u.id);
              }
            } catch {
              // basic user fetch failed
            }
          }
        }
        
        if (!profileData) {
          setError('Profile not found');
          setLoading(false);
          return;
        }
        
        setProfile(profileData);

        // Render the page immediately — secondary data (tracks, stats, follow
        // state) loads in the background so the profile + message button appear
        // right away instead of waiting on slow calls.
        setStats(prev => ({ ...prev, followers: profileData.follower_count || 0 }));
        setLoading(false);

        // Fetch tracks (background) — must be scoped to THIS profile's artist_id.
        axios.get(
          `${API_URL}/api/artists/tracks/${profileData.user_id}?limit=50`,
          { headers }
        ).then(tracksRes => {
          const t = tracksRes.data?.tracks || [];
          setTracks(t);
          const totalStreams = t.reduce((sum, x) => sum + (x.play_count || 0), 0);
          setStats(prev => ({ ...prev, totalStreams, trackCount: t.length }));
        }).catch(() => {});

        // Fetch top artists (background)
        axios.get(
          `${API_URL}/api/artists/profile/${profileData.user_id}/top-artists`,
          { headers }
        ).then(res => setTopArtists(res.data?.top_artists || [])).catch(() => {});

        // Fetch follower/following counts (background)
        axios.get(
          `${API_URL}/api/follow/counts/${profileData.user_id}`,
          { headers }
        ).then(res => {
          setStats(prev => ({
            ...prev,
            followers: res.data?.follower_count || 0,
            following: res.data?.following_count || 0
          }));
        }).catch(() => {});

        // Check if current user follows this profile (background)
        if (isAuthenticated && userId && user?.id !== userId) {
          axios.get(`${API_URL}/api/follow/status/${userId}`, { headers })
            .then(res => setIsFollowing(res.data?.is_following || false))
            .catch(() => {});
          axios.get(`${API_URL}/api/friends/status/${userId}`, { headers })
            .then(res => { setFriendStatus(res.data?.status || 'none'); setFriendReqId(res.data?.request_id || null); })
            .catch(() => {});
        }

      } catch (err) {
        console.error('Error fetching profile:', err);
        setError(err.response?.data?.detail || 'Failed to load profile');
        setLoading(false);
      }
    };

    fetchProfile();
  }, [targetUserId, userId, user?.id, isAuthenticated, navigate]);

  // Handle follow/unfollow
  const handleFollow = async () => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    setFollowLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      if (isFollowing) {
        await axios.delete(`${API_URL}/api/follow/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFollowing(false);
        setStats(prev => ({ ...prev, followers: prev.followers - 1 }));
      } else {
        await axios.post(`${API_URL}/api/follow/${userId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFollowing(true);
        setStats(prev => ({ ...prev, followers: prev.followers + 1 }));
      }
    } catch (err) {
      console.error('Follow error:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to update follow status';
      alert(errorMsg);
    } finally {
      setFollowLoading(false);
    }
  };

  // Handle friend request lifecycle: none → request → accept/cancel → unfriend
  const handleFriendAction = async () => {
    if (!isAuthenticated) { navigate('/auth'); return; }
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    setFriendLoading(true);
    try {
      let res;
      if (friendStatus === 'none') {
        res = await axios.post(`${API_URL}/api/friends/request/${userId}`, {}, { headers });
      } else if (friendStatus === 'pending_incoming') {
        res = await axios.post(`${API_URL}/api/friends/accept/${friendReqId}`, {}, { headers });
      } else if (friendStatus === 'pending_outgoing') {
        res = await axios.delete(`${API_URL}/api/friends/request/${userId}`, { headers });
      } else if (friendStatus === 'friends') {
        res = await axios.delete(`${API_URL}/api/friends/${userId}`, { headers });
      }
      const st = res?.data?.status || 'none';
      setFriendStatus(st);
      if (st !== 'pending_incoming') setFriendReqId(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Friend action failed');
    } finally {
      setFriendLoading(false);
    }
  };

  // Handle viewing stories when clicking on profile picture
  const handleViewStories = async () => {
    const storyUserId = userId || targetUserId;
    if (!storyUserId) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const res = await axios.get(
        `${API_URL}/api/radio/stories/user/${storyUserId}`,
        { headers }
      );
      
      const stories = res.data?.stories || [];
      if (stories.length > 0) {
        // Create storyUser object for StoryViewer
        setShowStoryViewer({
          user_id: storyUserId,
          user_name: profile?.stage_name || profile?.name,
          user_photo: profile?.profile_picture,
          is_artist: true,
          artist_name: profile?.stage_name,
          stories: stories
        });
      } else {
        // No stories to show
        console.log('No active stories for this user');
      }
    } catch (err) {
      console.error('Error fetching stories:', err);
    }
  };

  // Crop an image file to a centered square/cover and return a compressed JPEG data URL
  const processImage = (file, targetW, targetH) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');
        const scale = Math.max(targetW / img.width, targetH / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (targetW - w) / 2, (targetH - h) / 2, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleImageUpload = async (file, imageType) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please choose an image file');
      return;
    }
    setUploadingImage(imageType);
    try {
      const dims = imageType === 'cover' ? [1600, 600] : [512, 512];
      const imageData = await processImage(file, dims[0], dims[1]);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/artists/profile/upload-image`,
        { image_type: imageType, image_data: imageData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.url) {
        setProfile((prev) => ({
          ...prev,
          [imageType === 'cover' ? 'cover_photo' : 'profile_picture']: res.data.url
        }));
        if (imageType === 'profile') {
          patchUser({ profilePhoto: res.data.url });
        }
      }
    } catch (err) {
      console.error('Image upload error:', err);
      alert(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-2 border-[#FF007F] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  // Error state
  if (error || !profile) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-[#71717A]" />
          <h2 className="text-2xl font-bold text-white mb-2">{error || 'Profile Not Found'}</h2>
          <p className="text-[#A1A1AA] mb-6">This artist profile doesn&apos;t exist yet.</p>
          {isAuthenticated && !userId && (
            <button
              onClick={() => navigate('/artist-setup')}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold"
            >
              Create Artist Profile
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050507] pb-24">
      {/* Ambient accent glows to match the players */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full opacity-20 blur-3xl" style={{ background: '#FF007F' }} />
        <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full opacity-10 blur-3xl" style={{ background: '#00F0FF' }} />
      </div>
      {/* Top-right utility bar: notifications + messages (reachable everywhere) */}
      {isAuthenticated && (
        <div className="fixed top-3 right-3 z-50 flex items-center gap-2">
          <NotificationBell />
          <button
            onClick={() => navigate('/messages')}
            className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
            data-testid="profile-messages-btn"
            aria-label="Messages"
          >
            <MessageCircle className="w-5 h-5 text-white" />
          </button>
        </div>
      )}
      {/* Header with Cover */}
      <div className="relative">
        {/* Cover Photo */}
        <div className="h-40 md:h-56 bg-gradient-to-br from-[#FF007F]/30 to-[#00F0FF]/30 relative">
          {profile.cover_photo && (
            <img 
              src={profile.cover_photo} 
              alt="Cover" 
              className="w-full h-full object-cover"
            />
          )}
          {isOwnProfile && (
            <>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0], 'cover')}
                data-testid="cover-upload-input"
              />
              <button
                onClick={() => coverInputRef.current?.click()}
                disabled={uploadingImage === 'cover'}
                className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/50 hover:bg-black/70 text-white text-xs font-medium backdrop-blur-sm transition-colors"
                data-testid="change-cover-btn"
              >
                {uploadingImage === 'cover' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                {uploadingImage === 'cover' ? 'Uploading…' : 'Cover'}
              </button>
            </>
          )}
        </div>
        
        {/* Profile Picture */}
        <div className="absolute -bottom-16 left-4 md:left-8">
          <StoryGlowRing userId={userId || targetUserId} size="lg" onClick={handleViewStories}>
            <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-[#050505] bg-[#12101A]">
              {profile.profile_picture ? (
                <img 
                  src={profile.profile_picture} 
                  alt={profile.stage_name} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FF007F] to-[#00F0FF]">
                  <span className="text-white text-4xl font-bold">
                    {profile.stage_name?.[0]?.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </StoryGlowRing>
          {/* Change avatar button (own profile only) */}
          {isOwnProfile && (
            <>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0], 'profile')}
                data-testid="avatar-upload-input"
              />
              <button
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingImage === 'profile'}
                className="absolute -bottom-1 -left-1 w-9 h-9 bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center border-4 border-[#050505] hover:scale-105 transition-transform shadow-lg"
                title="Change profile picture"
                aria-label="Change profile picture"
                data-testid="change-avatar-btn"
              >
                {uploadingImage === 'profile'
                  ? <Loader2 className="w-4 h-4 text-white animate-spin" />
                  : <Camera className="w-4 h-4 text-white" />}
              </button>
            </>
          )}
          {/* Create Story button (own profile only) */}
          {isOwnProfile && (
            <button
              onClick={() => setShowStoryCreator(true)}
              className="absolute -bottom-1 -right-1 w-9 h-9 bg-gradient-to-br from-[#FF007F] to-[#8B5CF6] rounded-full flex items-center justify-center border-4 border-[#050505] hover:scale-105 transition-transform shadow-lg"
              title="Create story"
              aria-label="Create story"
              data-testid="profile-create-story-btn"
            >
              <Plus className="w-5 h-5 text-white" strokeWidth={3} />
            </button>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="absolute bottom-4 right-4 flex items-center gap-2">
          {isOwnProfile ? (
            <>
              <button
                onClick={openEditProfile}
                className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="edit-profile-btn"
                title="Edit name & bio"
              >
                <Edit2 className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={() => navigate('/settings')}
                className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <Settings className="w-5 h-5 text-white" />
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              {/* Message Button */}
              <button
                onClick={() => {
                  if (profile) {
                    openChat({
                      id: profile.user_id || userId,
                      name: profile.stage_name || profile.name,
                      username: profile.username,
                      profilePhoto: profile.profile_picture
                    });
                  }
                }}
                className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="message-btn"
              >
                <MessageCircle className="w-5 h-5 text-white" />
              </button>

              {/* Tip Button - artists only */}
              {(profile?.is_artist || profile?.stage_name) && (
                <button
                  onClick={() => setShowTip(true)}
                  className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#FF007F]/15 text-[#FF007F] hover:bg-[#FF007F]/25 transition-colors font-medium"
                  data-testid="profile-tip-btn"
                >
                  <Heart className="w-4 h-4" />
                  Tip
                </button>
              )}
              
              {/* Follow Button */}
              <button
                onClick={handleFollow}
                disabled={followLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors ${
                  isFollowing
                    ? 'bg-white/10 text-white hover:bg-white/20'
                    : 'bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white'
                }`}
                data-testid="follow-btn"
              >
              {followLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isFollowing ? (
                <>
                  <Check className="w-4 h-4" />
                  Following
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Follow
                </>
              )}
              </button>

              {/* Add Friend Button (mutual friend-request flow) */}
              <button
                onClick={handleFriendAction}
                disabled={friendLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors ${
                  friendStatus === 'friends'
                    ? 'bg-[#00F0FF]/15 text-[#00F0FF] hover:bg-[#00F0FF]/25'
                    : friendStatus === 'pending_incoming'
                    ? 'bg-gradient-to-r from-[#00F0FF] to-[#FF007F] text-white'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
                data-testid="friend-btn"
              >
                {friendLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : friendStatus === 'friends' ? (
                  <><Check className="w-4 h-4" /> Friends</>
                ) : friendStatus === 'pending_incoming' ? (
                  <><UserPlus className="w-4 h-4" /> Accept</>
                ) : friendStatus === 'pending_outgoing' ? (
                  <><Loader2 className="w-4 h-4" /> Requested</>
                ) : (
                  <><UserPlus className="w-4 h-4" /> Add Friend</>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Profile Info */}
      <div className="px-4 md:px-8 pt-20">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {profile.stage_name}
              </h1>
              {profile.verified && (
                <Award className="w-6 h-6 text-[#00F0FF]" />
              )}
            </div>
            
            {profile.role && (
              <span className="inline-block px-2 py-0.5 bg-[#FF007F]/20 text-[#FF007F] text-xs font-medium rounded-full mb-2">
                {profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
              </span>
            )}

            {(isOwnProfile || isOwner) && (
              <div className="flex items-center gap-2 mb-2" data-testid="role-switcher">
                <span className="text-[#71717A] text-xs">
                  {isOwnProfile ? 'Account type:' : 'Set account type:'}
                </span>
                <div className="inline-flex rounded-full bg-white/5 p-0.5">
                  {['listener', 'artist'].map((r) => {
                    const active = (profile.role || 'listener') === r;
                    return (
                      <button
                        key={r}
                        onClick={() => changeRole(r)}
                        disabled={roleUpdating || active}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                          active ? 'bg-[#00F0FF] text-[#050505]' : 'text-[#A1A1AA] hover:text-white disabled:opacity-50'
                        }`}
                        data-testid={`set-role-${r}`}
                      >
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </button>
                    );
                  })}
                </div>
                {roleUpdating && <Loader2 className="w-4 h-4 text-[#00F0FF] animate-spin" />}
              </div>
            )}
            
            {profile.location && (
              <p className="text-[#A1A1AA] text-sm flex items-center gap-1 mb-2">
                <MapPin className="w-4 h-4" />
                {profile.location}
              </p>
            )}
            
            {profile.genres?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {profile.genres.map((genre, i) => (
                  <span 
                    key={i}
                    className="px-2 py-1 bg-white/5 rounded-full text-xs text-[#A1A1AA]"
                  >
                    {genre}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bio */}
        {profile.bio && (
          <p className="text-[#A1A1AA] mt-4 leading-relaxed">
            {profile.bio}
          </p>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-4 mt-6 p-5 rounded-2xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.3)]">
          <StatCard 
            icon={Users} 
            value={stats.followers.toLocaleString()} 
            label="Followers" 
            color="bg-[#FF007F]"
          />
          <StatCard 
            icon={Heart} 
            value={stats.following.toLocaleString()} 
            label="Following" 
            color="bg-[#00F0FF]"
          />
          <StatCard 
            icon={Play} 
            value={stats.totalStreams.toLocaleString()} 
            label="Streams" 
            color="bg-purple-500"
          />
          <StatCard 
            icon={Music} 
            value={stats.trackCount.toString()} 
            label="Tracks" 
            color="bg-green-500"
          />
        </div>

        {/* Quick Actions for Own Profile */}
        {isOwnProfile && (
          <div className="flex items-center gap-3 mt-4">
            <Link
              to="/upload"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-medium"
              data-testid="upload-track-btn"
            >
              <Plus className="w-4 h-4" />
              Upload Track
            </Link>
            <Link
              to={`/epk/${encodeURIComponent(profile.stage_name)}`}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white font-medium hover:bg-white/10 transition-colors"
              data-testid="view-epk-btn"
            >
              <FileText className="w-4 h-4" />
              View EPK
            </Link>
            <Link
              to="/artist-dashboard"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white font-medium hover:bg-white/10 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
              Dashboard
            </Link>
            <Link
              to="/wallet"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#39FF14]/15 border border-[#39FF14]/30 text-[#39FF14] font-medium hover:bg-[#39FF14]/25 transition-colors"
              data-testid="profile-wallet-btn"
            >
              <Wallet className="w-4 h-4" />
              Wallet
            </Link>
          </div>
        )}

        {/* Tracks Section */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Music className="w-5 h-5 text-[#FF007F]" />
              Tracks
            </h2>
            {tracks.length > 5 && (
              <button className="text-[#00F0FF] text-sm font-medium flex items-center gap-1">
                See All <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {tracks.length > 0 ? (
            <div className="bg-[#12101A] rounded-2xl border border-white/5 overflow-hidden">
              {tracks.slice(0, 5).map((track, i) => (
                <TrackRow key={track.id} track={track} rank={i + 1} />
              ))}
            </div>
          ) : (
            <div className="bg-[#12101A] rounded-2xl border border-white/5 p-8 text-center">
              <Music className="w-12 h-12 mx-auto mb-3 text-[#71717A]" />
              <p className="text-[#71717A]">No tracks yet</p>
              {isOwnProfile && (
                <Link
                  to="/upload"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-xl bg-[#FF007F] text-white font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Upload Your First Track
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Media Gallery Section (Videos & Pictures) */}
        <div className="mt-8">
          <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-4">
            <Video className="w-5 h-5 text-[#00F0FF]" />
            Media
          </h2>
          <div className="bg-[#12101A] rounded-2xl border border-white/5 p-4">
            <MediaGallery 
              artistId={profile.id}
              canDelete={isOwnProfile}
              refreshKey={mediaRefreshKey}
            />
            {isOwnProfile && (
              <div className="mt-4">
                <MediaUpload 
                  onUploadComplete={() => {
                    // Refresh the gallery (the gallery also auto-polls while a
                    // video is still optimizing, so the overlay clears itself).
                    setMediaRefreshKey(k => k + 1);
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* My Clips Section */}
        <div className="mt-8">
          <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-4">
            <Play className="w-5 h-5 text-[#FF007F]" />
            {isOwnProfile ? 'My Clips' : 'Clips'}
          </h2>
          <div className="bg-[#12101A] rounded-2xl border border-white/5 p-4">
            <UserClipsGallery 
              userId={profile.user_id}
              canDelete={isOwnProfile}
            />
          </div>
        </div>

        {/* Top 5 Artists Section */}
        {topArtists.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-4">
              <Award className="w-5 h-5 text-[#00F0FF]" />
              {isOwnProfile ? 'My Top 5 Artists' : `${profile.stage_name}'s Top 5`}
            </h2>
            <div className="bg-[#12101A] rounded-2xl border border-white/5 p-4">
              {topArtists.map((artist, i) => (
                <TopArtistCard key={artist.id || i} artist={artist} rank={i + 1} />
              ))}
            </div>
          </div>
        )}

        {/* Social Links */}
        {profile.social_links && Object.values(profile.social_links).some(v => v) && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-4">
              <ExternalLink className="w-5 h-5 text-[#FF007F]" />
              Connect
            </h2>
            <div className="flex flex-wrap gap-3">
              {profile.social_links.instagram && (
                <a
                  href={profile.social_links.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium"
                >
                  Instagram
                </a>
              )}
              {profile.social_links.twitter && (
                <a
                  href={profile.social_links.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-xl bg-[#1DA1F2] text-white text-sm font-medium"
                >
                  Twitter
                </a>
              )}
              {profile.social_links.youtube && (
                <a
                  href={profile.social_links.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-xl bg-[#FF0000] text-white text-sm font-medium"
                >
                  YouTube
                </a>
              )}
              {profile.social_links.website && (
                <a
                  href={profile.social_links.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-xl bg-white/10 text-white text-sm font-medium"
                >
                  Website
                </a>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Story Viewer Modal */}
      {showStoryViewer && (
        <StoryViewer 
          storyUser={showStoryViewer}
          onClose={() => setShowStoryViewer(null)}        />
      )}

      {/* Story Creator Modal */}
      {showStoryCreator && (
        <StoryCreator
          onClose={() => setShowStoryCreator(false)}
          onSuccess={() => setShowStoryCreator(false)}
        />
      )}

      <TipModal
        open={showTip}
        onClose={() => setShowTip(false)}
        artistId={profile?.user_id || userId}
        artistName={profile?.stage_name || profile?.name}
      />

      {/* Edit Profile (name + bio) */}
      {showEditProfile && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => !savingProfile && setShowEditProfile(false)}
          data-testid="edit-profile-modal"
        >
          <div
            className="w-full max-w-md bg-[#12101A] border border-white/10 rounded-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white text-lg font-black" style={{ fontFamily: 'Unbounded, sans-serif' }}>Edit Profile</h3>
              <button
                onClick={() => setShowEditProfile(false)}
                disabled={savingProfile}
                className="text-white/50 hover:text-white disabled:opacity-40"
                data-testid="edit-profile-close"
              >✕</button>
            </div>

            <label className="block text-[#A1A1AA] text-sm mb-1.5">Name</label>
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              maxLength={60}
              placeholder="Your name"
              data-testid="edit-name-input"
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#00F0FF]/50 transition-colors"
            />

            <label className="block text-[#A1A1AA] text-sm mb-1.5 mt-4">Bio</label>
            <textarea
              value={editBio}
              onChange={(e) => setEditBio(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="Tell fans about yourself"
              data-testid="edit-bio-input"
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#00F0FF]/50 transition-colors resize-none"
            />
            <p className="text-right text-[10px] text-white/30 mt-1">{editBio.length}/500</p>

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowEditProfile(false)}
                disabled={savingProfile}
                className="flex-1 py-3 rounded-full border border-white/15 text-white/80 text-sm font-semibold hover:bg-white/5 disabled:opacity-40"
                data-testid="edit-profile-cancel"
              >
                Cancel
              </button>
              <button
                onClick={saveProfileEdits}
                disabled={savingProfile}
                className="flex-1 py-3 rounded-full text-white text-sm font-bold bg-gradient-to-r from-[#FF007F] to-[#00F0FF] disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="edit-profile-save"
              >
                {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save
              </button>
            </div>

            <button
              onClick={() => { setShowEditProfile(false); navigate('/artist-setup'); }}
              className="w-full mt-3 text-center text-xs text-white/40 hover:text-white/70 transition-colors"
              data-testid="edit-profile-more"
            >
              Edit genres, location &amp; links →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
